import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import api from '../services/api';
import 'react-quill/dist/quill.snow.css';
import './DocumentPanel.css';

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
};

function DocumentPanel({
  objectType,
  objectId,
  companyId,
  onClose,
  variant = 'drawer',
  isOpen = true,
  anchorTitle,
  className = '',
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ type: 'url', title: '', description: '', link_url: '' });
  const [saving, setSaving] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [files, setFiles] = useState([]);
  const [newVersionHtml, setNewVersionHtml] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [panelWidth, setPanelWidth] = useState(640);
  const [isResizing, setIsResizing] = useState(false);
  const [showModalEditor, setShowModalEditor] = useState(false);
  const [modalDraft, setModalDraft] = useState('');
  const fileInputRef = useRef(null);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [pinnedIds, setPinnedIds] = useState(() => []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const withFallback = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const editorModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'code'],
      ['blockquote'],
      ['clean'],
    ],
  }), []);

  const isReady = Boolean(objectType && objectId && companyId);
  const isInline = variant === 'inline';
  const isDrawer = variant === 'drawer';
  const isFullpage = variant === 'fullpage';
  const panelClass = `doc-panel ${isInline ? 'doc-panel-inline' : isFullpage ? 'doc-panel-full' : 'doc-panel-drawer'} ${className}`.trim();
  const panelStyle = isDrawer ? { width: `${panelWidth}px` } : undefined;

  useEffect(() => {
    const key = `doc_pins_${objectType || 'any'}_${objectId || 'any'}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) setPinnedIds(JSON.parse(saved));
    } catch (e) {
      // ignore storage errors
    }
  }, [objectType, objectId]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const onMove = (e) => {
      const delta = -e.movementX;
      setPanelWidth((prev) => Math.min(900, Math.max(420, prev + delta)));
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  const filteredDocs = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    const list = documents.filter((doc) => {
      const matchType = filterType === 'all' || doc.type === filterType;
      const matchSearch = !term || (doc.title || '').toLowerCase().includes(term) || (doc.description || '').toLowerCase().includes(term);
      return matchType && matchSearch;
    });
    return list.sort((a, b) => {
      const aPinned = pinnedIds.includes(a.id);
      const bPinned = pinnedIds.includes(b.id);
      if (aPinned && !bPinned) return -1;
      if (bPinned && !aPinned) return 1;
      return (b.id || 0) - (a.id || 0);
    });
  }, [documents, filterType, searchTerm, pinnedIds]);

  const togglePin = (docId) => {
    setPinnedIds((prev) => {
      const next = prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId];
      const key = `doc_pins_${objectType || 'any'}_${objectId || 'any'}`;
      try { localStorage.setItem(key, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    if (!isReady) {
      setDocuments([]);
      setSelectedDoc(null);
      setVersions([]);
      setFiles([]);
      return;
    }
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectId, companyId, isReady]);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await api.getDocuments({ objectType, objectId, companyId });
      setDocuments(resp.data || []);
    } catch (err) {
      setError(err.message || 'Error cargando documentos');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !isReady) return;
    try {
      setSaving(true);
      setError('');
      const createResp = await api.createDocument({
        object_type: objectType,
        object_id: objectId,
        type: form.type,
        title: form.title,
        description: form.description,
        link_url: form.type === 'url' ? form.link_url : undefined,
      }, companyId);
      const createdDoc = createResp?.data;

      if (form.type === 'attachment' && createdDoc?.id && attachmentFile) {
        try {
          await api.addDocumentFile(createdDoc.id, {
            filename: attachmentFile.name,
            mime_type: attachmentFile.type || undefined,
            size_bytes: attachmentFile.size,
            storage_key: `local/${Date.now()}-${attachmentFile.name}`,
          }, companyId);
        } catch (uploadErr) {
          setError(uploadErr.message || withFallback('documents.attachment_hint', 'Flujo de subida pendiente: conectaremos URL firmada para almacenar archivos.'));
        }
      }

      setForm({ type: form.type, title: '', description: '', link_url: '' });
      setAttachmentFile(null);
      fetchDocs();
    } catch (err) {
      setError(err.message || 'Error creando documento');
    } finally {
      setSaving(false);
    }
  };

  const openDoc = async (doc) => {
    setSelectedDoc(doc);
    setVersions([]);
    setFiles([]);
    setNewVersionHtml('');
    if (!doc) return;
    try {
      if (doc.type === 'internal') {
        const resp = await api.listDocumentVersions(doc.id, companyId);
        setVersions(resp.data || []);
      } else if (doc.type === 'attachment') {
        const resp = await api.listDocumentFiles(doc.id, companyId);
        setFiles(resp.data || []);
      }
    } catch (err) {
      setError(err.message || 'Error cargando detalle');
    }
  };

  const addVersion = async () => {
    if (!selectedDoc) return;
    const text = stripHtml(newVersionHtml);
    if (!text) return;
    try {
      setSaving(true);
      await api.addDocumentVersion(selectedDoc.id, {
        content_html: newVersionHtml,
        content_text: text,
      }, companyId);
      setNewVersionHtml('');
      openDoc(selectedDoc);
      fetchDocs();
    } catch (err) {
      setError(err.message || 'Error guardando versión');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const renderEmptyState = () => (
    <div className="doc-empty card">
      <div className="card-body">
        <p className="mb-1 fw-semibold">{withFallback('documents.panel_unavailable', 'Guarda primero para adjuntar documentación.')}</p>
        <p className="mb-0 text-muted small">{withFallback('documents.panel_unavailable_hint', 'Una vez guardado podrás añadir enlaces, adjuntos o notas ricas.')}</p>
      </div>
    </div>
  );

  const latestVersion = versions?.[0];

  const showInlineCreateForm = !isInline && !isFullpage;

  const renderCreateForm = () => (
    <div className="doc-create card mb-3">
      <div className="card-body">
        <form className="row g-2" onSubmit={handleCreate}>
          <div className="col-12 col-md-3">
            <label className="form-label mb-1">{withFallback('documents.type', 'Tipo')}</label>
            <select className="form-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="url">{withFallback('documents.type_url', 'Enlace')}</option>
              <option value="attachment">{withFallback('documents.type_attachment', 'Adjunto')}</option>
              <option value="internal">{withFallback('documents.type_internal', 'Nota interna')}</option>
            </select>
          </div>
          <div className="col-12 col-md-5">
            <label className="form-label mb-1">{withFallback('documents.title_field', 'Título')}</label>
            <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          {form.type === 'url' && (
            <div className="col-12 col-md-4">
              <label className="form-label mb-1">{withFallback('documents.link', 'URL')}</label>
              <input className="form-control" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://" />
            </div>
          )}
          {form.type === 'attachment' && (
            <div className="col-12 col-md-4 d-flex flex-column gap-1">
              <label className="form-label mb-1">{withFallback('documents.upload', 'Subir adjunto')}</label>
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
              >
                <span aria-hidden="true">📎</span>
                {attachmentFile ? attachmentFile.name : withFallback('documents.upload', 'Subir adjunto')}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="d-none"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setAttachmentFile(f);
                }}
              />
              <small className="text-muted">{withFallback('documents.attachment_hint', 'Flujo de subida pendiente: conectaremos URL firmada para almacenar archivos.')}</small>
            </div>
          )}
          <div className="col-12">
            <label className="form-label mb-1">{withFallback('documents.description', 'Descripción')}</label>
            <textarea className="form-control" rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}></textarea>
          </div>
          <div className="col-12 d-flex justify-content-end">
            <button className="btn btn-primary" type="submit" disabled={saving || !form.title}>
              {saving ? withFallback('action.saving', 'Guardando...') : withFallback('action.create', 'Crear')}
            </button>
          </div>
        </form>
        {form.type === 'attachment' && (
          <small className="text-muted">{withFallback('documents.attachment_hint', 'Subida real pendiente: se registrará el adjunto cuando integremos la URL firmada.')}</small>
        )}
        {form.type === 'internal' && (
          <small className="text-muted d-block mt-1">{withFallback('documents.rich_hint', 'Formato enriquecido: negrita, enlaces, listas y código.')}</small>
        )}
      </div>
    </div>
  );

  const renderDetailContent = () => {
    if (!selectedDoc) return null;
    return (
      <>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold">{selectedDoc.title}</div>
              <div className="small text-muted">{selectedDoc.description}</div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className="badge bg-light text-dark text-uppercase">{selectedDoc.type}</span>
              <span className="badge bg-secondary">v{selectedDoc.latest_version || 0}</span>
            </div>
          </div>
        </div>

        {selectedDoc.type === 'url' && (
          <div className="mb-3">
            <p className="mb-1"><strong>{withFallback('documents.link', 'URL')}:</strong> <a href={selectedDoc.link_url} target="_blank" rel="noreferrer noopener">{selectedDoc.link_url}</a></p>
          </div>
        )}

        {selectedDoc.type === 'attachment' && (
          <div className="mb-3">
            <p className="text-muted small mb-2">{withFallback('documents.attachment_hint', 'Subida real pendiente: se registrará el adjunto cuando integremos la URL firmada.')}</p>
            {files.length === 0 ? (
              <div className="text-muted small">{withFallback('documents.no_files', 'Aún no hay archivos subidos.')}</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{withFallback('documents.filename', 'Archivo')}</th>
                      <th>{withFallback('documents.version', 'Versión')}</th>
                      <th>{withFallback('documents.size', 'Tamaño')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((f) => (
                      <tr key={f.id}>
                        <td>{f.filename}</td>
                        <td>v{f.version}</td>
                        <td>{typeof f.size_bytes === 'number' ? `${Math.round((f.size_bytes || 0) / 1024)} KB` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {selectedDoc.type === 'internal' && (
          <div className="doc-internal">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label mb-0">{withFallback('documents.add_note', 'Añadir nota')}</label>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setModalDraft(newVersionHtml); setShowModalEditor(true); }}>
                    {withFallback('documents.expand_editor', 'Expandir editor')}
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={addVersion} disabled={saving || !stripHtml(newVersionHtml)}>
                    {withFallback('documents.save_note', 'Guardar versión')}
                  </button>
                </div>
              </div>
              <ReactQuill
                theme="snow"
                value={newVersionHtml}
                onChange={setNewVersionHtml}
                modules={editorModules}
                className="doc-quill doc-quill-tall"
                placeholder={withFallback('documents.editor_placeholder', 'Escribe con formato, añade enlaces, listas o código.')} />
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">{withFallback('documents.editor_hint', 'Ctrl/Cmd+B para negrita · Ctrl/Cmd+K para enlace')}</small>
                <span className="text-muted small">{withFallback('documents.rich_hint', 'Formato enriquecido: negrita, enlaces, listas y código.')}</span>
              </div>
            </div>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="fw-semibold">{withFallback('documents.history', 'Historial')}</div>
                {versions.length > 1 && (
                  <button className="btn btn-link btn-sm" onClick={() => setShowVersionsModal(true)}>
                    {withFallback('documents.view_all_versions', 'Ver todas las versiones')}
                  </button>
                )}
              </div>
              {versions.length === 0 ? (
                <div className="text-muted small">{withFallback('documents.no_versions', 'Sin versiones aún.')}</div>
              ) : (
                <div className="doc-version-latest">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-light text-dark">v{latestVersion?.version}</span>
                    <span className="text-muted small">{latestVersion?.created_at}</span>
                  </div>
                  {latestVersion?.content_html ? (
                    <div className="doc-version-content" dangerouslySetInnerHTML={{ __html: latestVersion.content_html }} />
                  ) : (
                    <div className="small">{latestVersion?.content_text}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={panelClass} style={panelStyle}>
      {isDrawer && (
        <div
          className="doc-resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
          role="presentation"
        />
      )}
      <div className="doc-panel-header">
        <div>
          <p className="doc-panel-eyebrow">{withFallback('documents.title', 'Documentación')}</p>
          <h4 className="doc-panel-title">{anchorTitle || withFallback('documents.subtitle', 'Adjunta archivos, enlaces o notas internas.')}</h4>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
          {objectType && objectId && !isFullpage && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/docs/${objectType}/${objectId}`)}
            >
              {withFallback('documents.open_fullscreen', 'Abrir en pantalla completa')}
            </button>
          )}
          {(isInline || isFullpage) && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowCreateModal(true)}
            >
              {withFallback('documents.smart_new_internal', 'Nueva nota interna')}
            </button>
          )}
          {onClose && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              {variant === 'inline' ? withFallback('documents.hide_panel', 'Ocultar') : withFallback('action.close', 'Cerrar')}
            </button>
          )}
        </div>
      </div>

      <div className="doc-panel-body">
        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        {!isReady ? (
          renderEmptyState()
        ) : (
          <>
            {showInlineCreateForm && renderCreateForm()}
            <div className="doc-grid">
              <div className="doc-list-panel card">
                <div className="card-body">
                  <div className="doc-list-filters">
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      placeholder={withFallback('documents.search_placeholder', 'Buscar por título o descripción')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="btn-group" role="group" aria-label={withFallback('documents.filter_types', 'Filtrar por tipo')}>
                      <button type="button" className={`btn btn-sm ${filterType === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilterType('all')}>All</button>
                      <button type="button" className={`btn btn-sm ${filterType === 'internal' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilterType('internal')}>{withFallback('documents.type_internal', 'Nota interna')}</button>
                      <button type="button" className={`btn btn-sm ${filterType === 'attachment' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilterType('attachment')}>{withFallback('documents.type_attachment', 'Adjunto')}</button>
                      <button type="button" className={`btn btn-sm ${filterType === 'url' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilterType('url')}>{withFallback('documents.type_url', 'Enlace')}</button>
                    </div>
                  </div>
                  {loading ? (
                    <div className="p-3 text-center text-muted">{withFallback('action.loading', 'Cargando...')}</div>
                  ) : filteredDocs.length === 0 ? (
                    <div className="p-3 text-center text-muted">{withFallback('documents.empty', 'Aún no hay documentos')}</div>
                  ) : (
                    <div className="list-group list-group-flush doc-list-scroll">
                      {filteredDocs.map((doc) => (
                        <button
                          key={doc.id}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start ${selectedDoc?.id === doc.id ? 'active' : ''}`}
                          onClick={() => {
                            openDoc(doc);
                            if (isInline) setShowDetailModal(true);
                          }}
                        >
                          <div>
                            <div className="fw-semibold d-flex align-items-center gap-2">
                              <span>{doc.title}</span>
                              <span className="badge bg-light text-dark text-uppercase">{doc.type}</span>
                              <span className="badge bg-secondary">v{doc.latest_version || 0}</span>
                            </div>
                            {doc.description && <div className="small text-muted mt-1 text-truncate">{doc.description}</div>}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePin(doc.id);
                              }}
                            >
                              {pinnedIds.includes(doc.id) ? '★' : '☆'}
                            </button>
                            {doc.type === 'url' && doc.link_url && (
                              <a className="btn btn-sm btn-outline-primary" href={doc.link_url} target="_blank" rel="noreferrer noopener">{withFallback('documents.open', 'Abrir')}</a>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {!isInline && (
                <div className="doc-detail-panel card">
                  {!selectedDoc ? (
                    <div className="card-body text-muted">
                      {withFallback('documents.select_hint', 'Selecciona un documento para ver el detalle.')}
                    </div>
                  ) : (
                    <div className="card-body">
                      <div className="d-flex justify-content-end mb-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedDoc(null)}>{withFallback('action.close', 'Cerrar')}</button>
                      </div>
                      {renderDetailContent()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showModalEditor && (
        <div className="doc-modal" role="dialog" aria-modal="true">
          <div className="doc-modal-backdrop" onClick={() => setShowModalEditor(false)} />
          <div className="doc-modal-content">
            <div className="doc-modal-header">
              <div>
                <p className="doc-panel-eyebrow mb-0">{withFallback('documents.title', 'Documentación')}</p>
                <h4 className="doc-panel-title mb-0">{withFallback('documents.expand_editor', 'Expandir editor')}</h4>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowModalEditor(false)}>
                  {withFallback('action.cancel', 'Cancelar')}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setNewVersionHtml(modalDraft);
                    setShowModalEditor(false);
                  }}
                >
                  {withFallback('documents.apply_changes', 'Aplicar al editor')}
                </button>
              </div>
            </div>
            <div className="doc-modal-body">
              <ReactQuill
                theme="snow"
                value={modalDraft}
                onChange={setModalDraft}
                modules={editorModules}
                className="doc-quill doc-quill-modal"
                placeholder={withFallback('documents.editor_placeholder', 'Escribe con formato, añade enlaces, listas o código.')} />
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && (
        <div className="doc-modal" role="dialog" aria-modal="true">
          <div className="doc-modal-backdrop" onClick={() => setShowVersionsModal(false)} />
          <div className="doc-modal-content">
            <div className="doc-modal-header">
              <div>
                <p className="doc-panel-eyebrow mb-0">{withFallback('documents.history', 'Historial')}</p>
                <h4 className="doc-panel-title mb-0">{selectedDoc?.title}</h4>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowVersionsModal(false)}>
                {withFallback('action.close', 'Cerrar')}
              </button>
            </div>
            <div className="doc-modal-body">
              {versions.length === 0 ? (
                <div className="text-muted small">{withFallback('documents.no_versions', 'Sin versiones aún.')}</div>
              ) : (
                <ul className="list-unstyled doc-versions">
                  {versions.map((v) => (
                    <li key={v.id} className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="badge bg-light text-dark">v{v.version}</span>
                        <span className="text-muted small">{v.created_at}</span>
                      </div>
                      {v.content_html ? (
                        <div className="doc-version-content" dangerouslySetInnerHTML={{ __html: v.content_html }} />
                      ) : (
                        <div className="small">{v.content_text}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {(isInline || isFullpage) && showCreateModal && (
        <div className="doc-modal" role="dialog" aria-modal="true">
          <div className="doc-modal-backdrop" onClick={() => setShowCreateModal(false)} />
          <div className="doc-modal-content">
            <div className="doc-modal-header">
              <div>
                <p className="doc-panel-eyebrow mb-0">{withFallback('documents.title', 'Documentación')}</p>
                <h4 className="doc-panel-title mb-0">{withFallback('action.create', 'Crear')}</h4>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowCreateModal(false)}>
                {withFallback('action.close', 'Cerrar')}
              </button>
            </div>
            <div className="doc-modal-body">
              {renderCreateForm()}
            </div>
          </div>
        </div>
      )}

      {isInline && showDetailModal && (
        <div className="doc-modal" role="dialog" aria-modal="true">
          <div className="doc-modal-backdrop" onClick={() => setShowDetailModal(false)} />
          <div className="doc-modal-content">
            <div className="doc-modal-header">
              <div>
                <p className="doc-panel-eyebrow mb-0">{withFallback('documents.title', 'Documentación')}</p>
                <h4 className="doc-panel-title mb-0">{selectedDoc?.title}</h4>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowDetailModal(false)}>
                {withFallback('action.close', 'Cerrar')}
              </button>
            </div>
            <div className="doc-modal-body">
              {renderDetailContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentPanel;
